<template>
  <v-card flat height="350">
    <v-card-text style="height: 100%; padding-top: 0;">
      <v-row align="center" justify="end" no-gutters class="mb-2">
        <v-btn
          elevation="0"
          x-small
          @click="showPastYear"
          :disabled="showingYear"
        >
          Année précédente
        </v-btn>
        <v-btn
          elevation="0"
          x-small
          @click="showPastWeek"
          :disabled="!showingYear"
        >
          Semaine précédente
        </v-btn>
      </v-row>
      <v-overlay :value="loading" absolute>
        <v-progress-circular indeterminate></v-progress-circular>
      </v-overlay>

      <canvas v-show="data.length > 0" id="analyticSalesBarChart"> </canvas>

      <v-row v-show="data.length <= 0" align="center" justify="center">
        <v-icon small class="mr-1">fa-exclamation-circle</v-icon>
        <span>Pas de données pour la période sélectionnée</span>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from "moment";
import Chart from "chart.js";
export default {
  name: "AnalyticSalesBarChart",
  props: {
    data: {
      type: Array,
      required: true
    },
    labels: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    }
  },
  mounted() {
    this.createChart();
  },
  data() {
    return {
      chart: undefined,
      showingYear: true
    };
  },
  methods: {
    createChart() {
      const ctx = document.getElementById("analyticSalesBarChart");
      this.chart = new Chart(ctx, {
        type: "bar",
        data: {
          datasets: [
            {
              label: "Ventes",
              data: this.data.map(x => {
                return { y: +x.net_sales, t: x.date };
              }),
              backgroundColor: "#003f5c",
              stack: "sales"
            },
            {
              label: "Marge de perte",
              data: this.data.map(x => {
                return { y: Math.ceil(+x.sales - x.net_sales), t: x.date };
              }),
              backgroundColor: "#003f5c99",
              stack: "sales"
            },
            {
              label: "Cost",
              data: this.data.map(x => {
                return { y: +x.cost, t: x.date };
              }),
              backgroundColor: "#f95d6a"
            },
            {
              label: "Année Précédente",
              data: this.data.map(x => {
                return { y: x.past_year_sales, t: x.date };
              }),
              backgroundColor: "#EF9A9A"
            },
            {
              label: "Semaine Précédente",
              hidden: true,
              data: this.data.map(x => {
                return { y: x.last_week_sales, t: x.date };
              }),
              backgroundColor: "#ffa600"
            },
            {
              label: "Ventes",
              data: this.data.map(x => {
                return { y: +x.sales, t: x.date };
              }),
              hidden: true
            }
          ],
          labels: this.labels
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          barValueSpacing: 1,
          legend: {
            display: false
          },
          tooltips: {
            mode: "label",
            callbacks: {
              label: function(t, d) {
                let dstLabel = d.datasets[t.datasetIndex].label;
                let yLabel = t.yLabel;
                if (t.datasetIndex === 0) {
                  let sales = d.datasets[5].data.find(x => x.t === t.xLabel);
                  if (sales) {
                    return `${dstLabel}: ${+sales.y}$`;
                  }
                } else if (t.datasetIndex === 1) {
                  let sales = d.datasets[5].data.find(x => x.t === t.xLabel);
                  if (sales && sales !== 0) {
                    let ratio = Math.ceil(
                      (Number(yLabel) / Number(sales.y)) * 100
                    );
                    return `${dstLabel}: ${yLabel}$ (${ratio}%)`;
                  }
                } else if (t.datasetIndex === 2) {
                  let sales = d.datasets[0].data.find(x => x.t === t.xLabel);
                  if (sales && sales !== 0) {
                    let cost = Math.ceil(
                      (Number(yLabel) / Number(sales.y)) * 100
                    );
                    return `${dstLabel}: ${yLabel}$ (${cost}%)`;
                  }
                }
                return `${dstLabel}: ${yLabel}$`;
              }
            }
          },
          scales: {
            gridLines: {
              color: "rgba(0, 0, 0, 0)"
            },
            yAxes: [
              {
                ticks: {
                  beginAtZero: true
                },
                gridLines: {
                  color: "rgba(0,0,0,0)",
                  display: false
                }
              }
            ],
            xAxes: [
              {
                offset: true,
                type: "time",
                distribution: "linear",
                time: {
                  unit: "day"
                },
                gridLines: {
                  offsetGridLines: false,
                  zeroLineColor: "rgba(0,0,0,0)",
                  color: "rgba(0,0,0,0)"
                },
                ticks: {
                  source: "labels",
                  callback: function(value, index, values) {
                    return `${value} (${moment(
                      values[index].value
                    ).isoWeek()}-${moment(values[index].value).isoWeekday()})`;
                  }
                }
              }
            ]
          }
        }
      });
    },
    updateChart() {
      this.chart.data.datasets[0].data = this.data.map(x => {
        return { y: +x.net_sales, t: x.date };
      });
      this.chart.data.datasets[1].data = this.data.map(x => {
        return { y: Math.ceil(+x.sales - x.net_sales), t: x.date };
      });
      this.chart.data.datasets[2].data = this.data.map(x => {
        return { y: +x.cost, t: x.date };
      });
      this.chart.data.datasets[3].data = this.data.map(x => {
        return { y: x.past_year_sales, t: x.date };
      });
      this.chart.data.datasets[4].data = this.data.map(x => {
        return { y: x.last_week_sales, t: x.date };
      });
      this.chart.data.datasets[5].data = this.data.map(x => {
        return { y: +x.sales, t: x.date };
      });

      this.chart.data.labels = this.labels;
      this.chart.update();
    },
    showPastYear() {
      this.showingYear = true;
      this.chart.data.datasets[3].hidden = false;
      this.chart.data.datasets[4].hidden = true;
      this.chart.update();
    },
    showPastWeek() {
      this.showingYear = false;
      this.chart.data.datasets[3].hidden = true;
      this.chart.data.datasets[4].hidden = false;
      this.chart.update();
    }
  },
  watch: {
    loading() {
      this.updateChart();
    }
  }
};
</script>

<style lang="scss" scoped></style>
